// src/pages/stylist/DashboardPage.tsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { format, startOfWeek, addDays, addMonths, isSameDay, isToday, parseISO, startOfDay } from 'date-fns';
import { useAppSelector, useAppDispatch } from '../../hooks/useRedux';
import { getCurrentUser } from '../../store/slices/authSlice';
import { Appointment } from '../../types/appointment';
import { StylistStats, Review } from '../../types/stylist';
import { formatCurrency } from '../../utils/formatters';
import appointmentService from '../../services/appointmentService';
import realtimeService from '../../services/realtimeService';
import {
  patchAppointmentGroups,
  patchAppointmentList,
  AppointmentUpdateEvent,
} from '../../utils/appointmentRealtime';
import { useDocumentVisibleRefetch } from '../../hooks/useDocumentVisibleRefetch';
import { getStylistReviews, ReviewsResponse } from '../../services/reviewService';
import { CalendarIcon, StarIcon, ArrowRightIcon, AlertIcon } from '../../components/common/Icons';
import { useUI } from '../../context/UIContext';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import DiscountBadge from '../../components/common/DiscountBadge';
import { selectUpcomingHomeAppointments } from '../../utils/getAppointmentBucket';
import { publicAppointmentRef } from '../../utils/publicRefs';
import { getWelcomeDisplayName } from '../../utils/welcomeDisplayName';

/** Next upcoming appointments shown on the stylist home widget (matches mobile: soonest one). */
const UPCOMING_APPOINTMENT_WIDGET_LIMIT = 1;

/** Normalize API review stats for the summary card (handles missing stats, BSON decimals, bad aggregates). */
const reviewStatsForDashboardCard = (payload: ReviewsResponse): { averageRating: number; totalReviews: number } => {
  const rs = payload.stats;
  const pageReviews = payload.reviews ?? [];
  const paginationTotal = Number(payload.pagination?.total);
  const safePaginationTotal = Number.isFinite(paginationTotal) ? paginationTotal : 0;

  let totalReviews = Number(rs?.totalReviews);
  if (!Number.isFinite(totalReviews)) totalReviews = 0;

  let rawAvg: unknown = rs?.averageRating;
  if (rawAvg != null && typeof rawAvg === 'object' && rawAvg !== null && '$numberDecimal' in (rawAvg as object)) {
    rawAvg = parseFloat(String((rawAvg as { $numberDecimal: string }).$numberDecimal));
  }
  let averageRating = typeof rawAvg === 'number' ? rawAvg : Number(rawAvg);
  if (!Number.isFinite(averageRating)) averageRating = 0;

  if (pageReviews.length > 0 && totalReviews === 0 && safePaginationTotal > 0) {
    totalReviews = safePaginationTotal;
  }

  // Only infer average from the current page when it contains every review (avoid wrong global avg).
  const pageCoversAllReviews =
    safePaginationTotal > 0 && pageReviews.length >= safePaginationTotal;
  if (pageReviews.length > 0 && averageRating === 0 && pageCoversAllReviews) {
    const scores = pageReviews
      .map((r) => Number(r.rating?.overall))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (scores.length > 0) {
      averageRating = scores.reduce((a, b) => a + b, 0) / scores.length;
    }
  }

  return { averageRating, totalReviews };
};

/** Treat as inactive for dashboards / calendar counts (not shown as a booking). */
const isAppointmentCancelled = (apt: Appointment): boolean => apt?.status === 'cancelled';

const getAppointmentStatusLabel = (status: Appointment['status'] | undefined): string => {
  if (!status) return 'Pending';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

// Helper function to calculate stylist stats from appointments
const calculateStylistStats = (appointments: Appointment[]): StylistStats => {
  try {
    if (!Array.isArray(appointments)) {
      console.warn('Invalid appointments array provided to calculateStylistStats');
      return {
        appointmentsToday: 0,
        appointmentsThisWeek: 0,
        appointmentsThisMonth: 0,
        earningsToday: 0,
        earningsThisWeek: 0,
        earningsThisMonth: 0,
        totalAppointments: 0,
        totalEarnings: 0,
        completionRate: 0,
        averageRating: 0,
      };
    }

    const now = new Date();
    const todayStart = startOfDay(now);
    const todayEndExclusive = addDays(todayStart, 1);
    // Match Weekly Overview strip: Monday-start week, calendar days in local tz
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEndExclusive = addDays(weekStart, 7);
    const monthStart = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
    const monthEndExclusive = addMonths(monthStart, 1);

    let appointmentsToday = 0;
    let appointmentsThisWeek = 0;
    let appointmentsThisMonth = 0;
    let earningsToday = 0;
    let earningsThisWeek = 0;
    let earningsThisMonth = 0;
    let totalEarnings = 0;
    let completedCount = 0;

    appointments.forEach((apt) => {
      try {
        if (!apt || !apt.dateTime) {
          console.warn('Invalid appointment data:', apt);
          return;
        }

        const aptDate = parseISO(apt.dateTime);
        if (isNaN(aptDate.getTime())) {
          console.warn('Invalid date format:', apt.dateTime);
          return;
        }

        const aptDayStart = startOfDay(aptDate);
        const netShare = Number(apt.paymentDetails?.stylistShare);
        const aptPrice =
          Number.isFinite(netShare) && netShare > 0
            ? netShare
            : 0;
        const isCompleted = apt.status === 'completed' && apt.paymentStatus === 'paid';
        const cancelled = isAppointmentCancelled(apt);

        // Count appointments in each window (exclude cancelled — not active bookings)
        if (!cancelled && aptDayStart >= todayStart && aptDayStart < todayEndExclusive) {
          appointmentsToday++;
          if (isCompleted) earningsToday += aptPrice;
        }
        if (!cancelled && aptDayStart >= weekStart && aptDayStart < weekEndExclusive) {
          appointmentsThisWeek++;
          if (isCompleted) earningsThisWeek += aptPrice;
        }
        if (!cancelled && aptDayStart >= monthStart && aptDayStart < monthEndExclusive) {
          appointmentsThisMonth++;
          if (isCompleted) earningsThisMonth += aptPrice;
        }
        if (isCompleted) {
          totalEarnings += aptPrice;
          completedCount++;
        }
      } catch (error) {
        console.error('Error processing appointment in calculateStylistStats:', apt, error);
      }
    });

    const totalAppointments = appointments.length;
    const nonCancelledCount = appointments.filter((apt) => !isAppointmentCancelled(apt)).length;
    const completionRate =
      nonCancelledCount > 0 ? (completedCount / nonCancelledCount) * 100 : 0;

    return {
      appointmentsToday,
      appointmentsThisWeek,
      appointmentsThisMonth,
      earningsToday,
      earningsThisWeek,
      earningsThisMonth,
      totalAppointments,
      totalEarnings,
      completionRate: Math.round(completionRate),
      averageRating: 0, // Will be fetched separately from reviews
    };
  } catch (error) {
    console.error('Error calculating stylist stats:', error);
    return {
      appointmentsToday: 0,
      appointmentsThisWeek: 0,
      appointmentsThisMonth: 0,
      earningsToday: 0,
      earningsThisWeek: 0,
      earningsThisMonth: 0,
      totalAppointments: 0,
      totalEarnings: 0,
      completionRate: 0,
      averageRating: 0,
    };
  }
};

// Helper function to organize appointments by week
const organizeAppointmentsByWeek = (appointments: Appointment[]): { [key: string]: Appointment[] } => {
  try {
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: 1 }); // Monday
    const weeklyAppointments: { [key: string]: Appointment[] } = {};

    // Initialize each day of the week
    for (let i = 0; i < 7; i++) {
      const currentDay = addDays(weekStart, i);
      const dayStart = startOfDay(currentDay); // Normalize to start of day in local timezone
      weeklyAppointments[format(dayStart, 'yyyy-MM-dd')] = [];
    }

    // Group appointments by date (only for the current week)
    if (Array.isArray(appointments)) {
      appointments.forEach((appointment) => {
        try {
          // Validate appointment has required fields
          if (!appointment || !appointment.dateTime) {
            console.warn('Invalid appointment data:', appointment);
            return;
          }

          // Parse the appointment date and normalize to start of day in local timezone
          const appointmentDateTime = parseISO(appointment.dateTime);
          if (isNaN(appointmentDateTime.getTime())) {
            console.warn('Invalid date format:', appointment.dateTime);
            return;
          }

          const appointmentDayStart = startOfDay(appointmentDateTime);
          const appointmentDate = format(appointmentDayStart, 'yyyy-MM-dd');

          if (weeklyAppointments[appointmentDate] && !isAppointmentCancelled(appointment)) {
            weeklyAppointments[appointmentDate].push(appointment);
          }
        } catch (error) {
          console.error('Error parsing appointment date:', appointment?.dateTime, error);
        }
      });
    }

    return weeklyAppointments;
  } catch (error) {
    console.error('Error organizing appointments by week:', error);
    return {};
  }
};

const StylistDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { stylistApproval: isStylistApprovalEnabled } = useFeatureFlags();
  const { user } = useAppSelector((state) => state.auth);
  // Use stable user ID instead of whole user object to prevent unnecessary re-renders
  const userId = user?.id || user?._id;

  // Refresh the user profile on mount so approvalStatus is always current.
  // This ensures the pending-approval banner clears as soon as the stylist
  // loads the dashboard after an admin has approved them.
  useEffect(() => {
    dispatch(getCurrentUser());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { showSnackbar } = useUI();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [weeklyAppointments, setWeeklyAppointments] = useState<{ [key: string]: Appointment[] }>({});
  const [stylistStats, setStylistStats] = useState<StylistStats | null>(null);
  const [recentReviews, setRecentReviews] = useState<Review[]>([]);
  const [reviewStats, setReviewStats] = useState<{ averageRating: number; totalReviews: number }>({
    averageRating: 0,
    totalReviews: 0,
  });
  
  // Use ref to track if a request is in progress to prevent infinite loops
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const hasLoadedOnceRef = useRef(false); // soft-refresh: keep prior UI after first successful load
  const lastUserIdRef = useRef<string | undefined>(undefined);
  // Store showSnackbar in ref to avoid including it in dependency array (prevents infinite loops)
  const showSnackbarRef = useRef(showSnackbar);
  
  // Update ref when showSnackbar changes
  useEffect(() => {
    showSnackbarRef.current = showSnackbar;
  }, [showSnackbar]);

  useEffect(() => {
    let isMounted = true; // Prevent state updates if component unmounts
    let requestAborted = false; // Track if request was aborted

    // Allow reload on each visit (including back-navigation from Details).
    hasLoadedRef.current = false;

    // Reset once-loaded flag when user changes so the next user gets a full first-load UI.
    if (lastUserIdRef.current !== userId) {
      console.log('[Dashboard] User ID changed, resetting refs...', { old: lastUserIdRef.current, new: userId });
      hasLoadedOnceRef.current = false;
      isLoadingRef.current = false;
      lastUserIdRef.current = userId;
    }

    const loadData = async () => {
      // Prevent multiple simultaneous requests
      if (isLoadingRef.current) {
        console.log('[Dashboard] Request already in progress, skipping...');
        return;
      }

      // Prevent loading if we've already loaded for this user
      if (hasLoadedRef.current && lastUserIdRef.current === userId) {
        console.log('[Dashboard] Already loaded for this user, skipping...');
        return;
      }

      // Don't load if no user ID
      if (!userId) {
        console.log('[Dashboard] No user ID, skipping load...');
        setIsLoading(false);
        return;
      }

      const isInitialLoad = !hasLoadedOnceRef.current;

      try {
        console.log('[Dashboard] Starting data load...', { userId, isInitialLoad });
        isLoadingRef.current = true;
        // Soft-refresh on back-nav: keep existing cards/stats visible; skeleton only on first load.
        if (isInitialLoad) {
          setIsLoading(true);
        }
        setError(null);

        // Fetch all appointments
        const appointments = await appointmentService.getAppointments();
        
        if (!isMounted || requestAborted) {
          console.log('[Dashboard] Component unmounted or request aborted, stopping...');
          isLoadingRef.current = false;
          return;
        }

        // Validate appointments data
        if (!Array.isArray(appointments)) {
          throw new Error('Invalid appointments data received');
        }
        
        // Next upcoming appointment(s) — same semantics as mobile HomeScreen
        const upcomingAppts = selectUpcomingHomeAppointments(appointments, {
          limit: UPCOMING_APPOINTMENT_WIDGET_LIMIT,
        });
        setUpcomingAppointments(upcomingAppts);

        // Organize weekly appointments
        const weeklyAppts = organizeAppointmentsByWeek(appointments);
        setWeeklyAppointments(weeklyAppts);

        // Calculate stylist stats from appointments (no backend endpoint exists yet)
        const stats = calculateStylistStats(appointments);
        setStylistStats(stats);

        // Fetch recent reviews (limit to 3)
        if (userId) {
          try {
            const reviewsResponse = await getStylistReviews(userId, { limit: 3 });
            
            if (!isMounted) return;

            // Validate and map API reviews to component Review type
            if (reviewsResponse && Array.isArray(reviewsResponse.reviews)) {
              setReviewStats(reviewStatsForDashboardCard(reviewsResponse));

              const mappedReviews: Review[] = [];
              
              reviewsResponse.reviews.slice(0, 3).forEach(review => {
                try {
                  const photo = review.client?.profilePhoto;
                  mappedReviews.push({
                    id: review._id || '',
                    clientId: review.client?._id || '',
                    clientName: review.client?.name || 'Unknown Client',
                    clientPhoto: photo || undefined,
                    rating: Number(review.rating?.overall) || 0,
                    comment: review.review?.text || '',
                    date: review.createdAt || new Date().toISOString(),
                  });
                } catch (error) {
                  console.error('Error mapping review:', review, error);
                }
              });
              
              setRecentReviews(mappedReviews);
            }
          } catch (reviewError) {
            console.error('Error fetching reviews:', reviewError);
            // Don't fail the entire dashboard if reviews fail
            setRecentReviews([]);
            setReviewStats({ averageRating: 0, totalReviews: 0 });
          }
        }

        if (!isMounted || requestAborted) {
          console.log('[Dashboard] Component unmounted or request aborted after success, stopping...');
          isLoadingRef.current = false;
          return;
        }

        hasLoadedRef.current = true;
        hasLoadedOnceRef.current = true;
        isLoadingRef.current = false;
        setIsLoading(false);
        console.log('[Dashboard] Data loaded successfully');
      } catch (error: any) {
        if (!isMounted || requestAborted) {
          console.log('[Dashboard] Component unmounted or request aborted during error, stopping...');
          isLoadingRef.current = false;
          return;
        }
        
        isLoadingRef.current = false;
        setIsLoading(false);
        
        // Handle 429 (Too Many Requests) errors gracefully
        if (error?.response?.status === 429) {
          console.warn('[Dashboard] 429 error - too many requests');
          const errorMessage = 'Too many requests. Please wait a moment and refresh the page.';
          setError(errorMessage);
          // Mark as loaded to prevent retries (even though it failed)
          hasLoadedRef.current = true;
          // Only show snackbar once
          if (lastUserIdRef.current === userId) {
            showSnackbarRef.current(errorMessage, 'warning');
          }
          // Don't retry immediately - let the user refresh manually
          return;
        }
        
        // Handle other errors
        console.error('[Dashboard] Error loading dashboard data:', error);
        const errorMessage = error?.message || 'Failed to load dashboard data. Please try again.';
        setError(errorMessage);
        // Mark as loaded to prevent infinite retries
        hasLoadedRef.current = true;
        // Only show snackbar once
        if (lastUserIdRef.current === userId) {
          showSnackbarRef.current(errorMessage, 'error');
        }
      }
    };

    // Only load data if user exists and we haven't loaded yet for this user
    // Use a small delay to debounce rapid re-renders during login
    const loadTimer = setTimeout(() => {
      if (userId) {
        if (!hasLoadedRef.current && !isLoadingRef.current) {
          console.log('[Dashboard] Conditions met, calling loadData()', { 
            userId, 
            hasLoaded: hasLoadedRef.current, 
            isLoading: isLoadingRef.current 
          });
          loadData();
        } else {
          console.log('[Dashboard] Skipping loadData()', { 
            userId, 
            hasLoaded: hasLoadedRef.current, 
            isLoading: isLoadingRef.current 
          });
        }
      } else {
        console.log('[Dashboard] No userId, resetting state');
        setIsLoading(false);
        hasLoadedRef.current = false; // Reset when user logs out
        hasLoadedOnceRef.current = false;
        isLoadingRef.current = false; // Reset loading flag
        lastUserIdRef.current = undefined;
      }
    }, 100); // 100ms debounce to prevent rapid re-renders from triggering multiple loads

    return () => {
      console.log('[Dashboard] Cleanup: unmounting component');
      clearTimeout(loadTimer);
      isMounted = false;
      requestAborted = true;
      // Reset isLoadingRef on cleanup to prevent stuck state
      isLoadingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, location.key]); // location.key: refetch when navigating back to dashboard

  const refreshStylistAppointments = useCallback(async () => {
    const appointments = await appointmentService.getAppointments();
    if (!Array.isArray(appointments)) return;
    setUpcomingAppointments(selectUpcomingHomeAppointments(appointments, {
      limit: UPCOMING_APPOINTMENT_WIDGET_LIMIT,
    }));
    setWeeklyAppointments(organizeAppointmentsByWeek(appointments));
    setStylistStats(calculateStylistStats(appointments));
  }, []);

  const stylistRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleStylistAppointmentRefresh = useCallback(() => {
    if (stylistRefreshTimerRef.current) clearTimeout(stylistRefreshTimerRef.current);
    stylistRefreshTimerRef.current = setTimeout(() => {
      stylistRefreshTimerRef.current = null;
      refreshStylistAppointments().catch((error) => {
        console.error('[Dashboard] Appointment refresh failed:', error);
      });
    }, 400);
  }, [refreshStylistAppointments]);

  useEffect(() => {
    return realtimeService.onAppointmentUpdate((data: AppointmentUpdateEvent) => {
      setUpcomingAppointments((prev) => patchAppointmentList(prev, data));
      setWeeklyAppointments((prev) => patchAppointmentGroups(prev, data));
      scheduleStylistAppointmentRefresh();
    });
  }, [scheduleStylistAppointmentRefresh]);

  useDocumentVisibleRefetch(() => {
    scheduleStylistAppointmentRefresh();
  }, Boolean(userId));

  useEffect(() => () => {
    if (stylistRefreshTimerRef.current) clearTimeout(stylistRefreshTimerRef.current);
  }, []);

  const formatAppointmentDate = (dateTime: string): string => {
    try {
      if (!dateTime) return 'Date TBD';
      const appointmentDate = parseISO(dateTime);
      if (isNaN(appointmentDate.getTime())) {
        return 'Invalid Date';
      }
      if (isToday(appointmentDate)) {
        return 'Today';
      }
      return format(appointmentDate, 'EEE, MMM d');
    } catch (error) {
      console.error('Error formatting appointment date:', dateTime, error);
      return 'N/A';
    }
  };

  const formatAppointmentTime = (dateTime: string): string => {
    try {
      if (!dateTime) {
        return 'N/A';
      }
      const appointmentTime = parseISO(dateTime);
      if (isNaN(appointmentTime.getTime())) {
        console.warn('Invalid date format:', dateTime);
        return 'Invalid Date';
      }
      return format(appointmentTime, 'h:mm a');
    } catch (error) {
      console.error('Error formatting appointment time:', dateTime, error);
      return 'N/A';
    }
  };

  const renderWeeklyCalendar = () => {
    if (isLoading) {
      return <div className="weekly-calendar-modern skeleton"></div>;
    }
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const currentDay = addDays(weekStart, i);
      const dayStart = startOfDay(currentDay); // Normalize to start of day
      const dateKey = format(dayStart, 'yyyy-MM-dd');
      const dayAppointments = weeklyAppointments[dateKey] || [];
      const isToday = isSameDay(currentDay, today);
      const appointmentCount = dayAppointments.length;
      
      const dayLabel = format(currentDay, 'EEEE, MMM d');
      weekDays.push(
        <div
          key={i}
          className={`weekly-calendar-modern__day ${isToday ? 'weekly-calendar-modern__day--today' : ''} ${appointmentCount > 0 ? 'weekly-calendar-modern__day--has-appointments' : ''}`}
          onClick={appointmentCount > 0 ? () => navigate('/stylist/appointments') : undefined}
          style={appointmentCount > 0 ? { cursor: 'pointer' } : undefined}
          role={appointmentCount > 0 ? 'button' : undefined}
          tabIndex={appointmentCount > 0 ? 0 : undefined}
          aria-label={
            appointmentCount > 0
              ? `${dayLabel}, ${appointmentCount} appointment${appointmentCount === 1 ? '' : 's'}. Open appointments.`
              : undefined
          }
          onKeyDown={
            appointmentCount > 0
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/stylist/appointments');
                  }
                }
              : undefined
          }
        >
          <span className={`weekly-calendar-modern__day-name ${isToday ? 'weekly-calendar-modern__day-name--today' : ''}`}>
            {format(currentDay, 'EEE')}
          </span>
          <span className={`weekly-calendar-modern__day-date ${isToday ? 'weekly-calendar-modern__day-date--today' : ''}`}>
            {format(currentDay, 'd')}
          </span>
          <div className="weekly-calendar-modern__status" aria-hidden="true">
            {appointmentCount > 0 ? (
              <span className="weekly-calendar-modern__badge">
                {appointmentCount}
              </span>
            ) : (
              <span className="weekly-calendar-modern__idle" />
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="weekly-calendar-modern">
        <div className="weekly-calendar-modern__grid">
          {weekDays}
        </div>
      </div>
    );
  };

      // If no user, show loading or redirect message
      if (!user && !isLoading) {
        return (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
            flexDirection: 'column',
            gap: '16px',
            padding: '40px'
          }}>
            <p style={{ color: '#666', fontSize: '16px' }}>Please wait while we load your dashboard...</p>
          </div>
        );
      }

      // Show error state
      if (error && !isLoading) {
        return (
          <div className="stylist-dashboard-modern">
            <div className="empty-state-modern">
              <div className="empty-state-modern__icon">
                <AlertIcon size={48} color="#C0392B" />
              </div>
              <h3 className="empty-state-modern__title">Error Loading Dashboard</h3>
              <p className="empty-state-modern__description">{error}</p>
              <button
                className="empty-state-modern__button"
                onClick={() => {
                  // Reset refs and retry loading
                  hasLoadedRef.current = false;
                  isLoadingRef.current = false;
                  setError(null);
                  // This will trigger the useEffect to run again
                  if (user) {
                    // Force re-run by clearing the hasLoaded flag
                    // The useEffect will see user exists and hasLoadedRef is false, so it will load
                    window.location.reload();
                  }
                }}
              >
                Reload Page
              </button>
            </div>
          </div>
        );
      }

  return (
    <div className="stylist-dashboard-modern">
      {/* Header Section */}
      <div className="dashboard-header-modern">
        <div className="dashboard-header-modern__content">
          <h1 className="dashboard-header-modern__title">
            {(() => {
              const displayName = getWelcomeDisplayName(user);
              return displayName ? `Hello, ${displayName}` : 'Hello';
            })()}
          </h1>
          <p className="dashboard-header-modern__subtitle">
            Here's your business overview for today
          </p>
        </div>
      </div>

      {isStylistApprovalEnabled && user?.approvalStatus === 'pending' && (
        <div className="approval-pending-banner">
          <span className="approval-pending-banner__icon" aria-hidden="true">⏳</span>
          <div className="approval-pending-banner__text">
            <strong>Your account is under review for client visibility.</strong>
            {' '}You can continue setting up your profile, services, and availability while we review your account.
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="stats-grid-modern">
        {isLoading ? (
          <>
            {Array(3).fill(0).map((_, i) => (
              <div key={i} className="stat-card-modern skeleton"></div>
            ))}
          </>
        ) : stylistStats ? (
          <>
            <div className="stat-card-modern stat-card-modern--primary">
              <div className="stat-card-modern__icon-wrapper">
                <CalendarIcon />
              </div>
              <div className="stat-card-modern__content">
                <div className="stat-card-modern__value">{stylistStats.appointmentsToday || 0}</div>
                <div className="stat-card-modern__label">Today's appointments</div>
              </div>
              <div className="stat-card-modern__accent"></div>
            </div>
            <div className="stat-card-modern stat-card-modern--success" title="Active bookings Mon–Sun (cancelled excluded)">
              <div className="stat-card-modern__icon-wrapper">
                <CalendarIcon />
              </div>
              <div className="stat-card-modern__content">
                <div className="stat-card-modern__value">{stylistStats.appointmentsThisWeek || 0}</div>
                <div className="stat-card-modern__label">This week</div>
              </div>
              <div className="stat-card-modern__accent"></div>
            </div>
            <div className="stat-card-modern stat-card-modern--money">
              <div className="stat-card-modern__icon-wrapper">
                <StarIcon filled />
              </div>
              <div className="stat-card-modern__content">
                <div className="stat-card-modern__value">
                  {reviewStats.totalReviews > 0 ? reviewStats.averageRating.toFixed(1) : '—'}
                </div>
                <div className="stat-card-modern__label">
                  {reviewStats.totalReviews > 0
                    ? `Avg rating · ${reviewStats.totalReviews} review${reviewStats.totalReviews === 1 ? '' : 's'}`
                    : 'Avg rating (no reviews yet)'}
                </div>
              </div>
              <div className="stat-card-modern__accent"></div>
            </div>
          </>
        ) : (
          <>
            {Array(3).fill(0).map((_, i) => (
              <div key={i} className="stat-card-modern skeleton"></div>
            ))}
          </>
        )}
      </div>

      <div className="dashboard-content-modern">

        {/* Upcoming Appointment — next non-past (mobile HomeScreen semantics); limit 1 to match singular card */}
        <div className="section-modern">
          <div className="section-modern__header">
            <h2 className="section-modern__title">Upcoming Appointment</h2>
            {upcomingAppointments.length > 0 && (
              <button
                className="section-modern__action"
                onClick={() => navigate('/stylist/appointments')}
              >
                View All
                <ArrowRightIcon />
              </button>
            )}
          </div>

          <div className="section-modern__content">
            {isLoading ? (
              <>
                <div className="appointment-card-modern skeleton"></div>
              </>
            ) : upcomingAppointments.length > 0 ? (
              upcomingAppointments.map((appointment) => {
                const servicePrice = appointment.service?.finalPrice ?? appointment.service?.originalPrice ?? 0;
                const addons = appointment.addons ?? [];
                const addonsTotal = addons.reduce(
                  (sum, addon) => sum + (typeof addon.priceCents === 'number' ? addon.priceCents / 100 : 0),
                  0
                );
                const totalPrice = servicePrice + addonsTotal;
                const hasAddons = addons.length > 0;
                return (
                  <div
                    key={appointment.id || appointment._id}
                    className="appointment-card-modern"
                    onClick={() => navigate(`/stylist/appointment/${publicAppointmentRef(appointment)}`)}
                  >
                    <div className="appointment-card-modern__date">
                      <div className="appointment-card-modern__date-day">{formatAppointmentDate(appointment.dateTime)}</div>
                      <div className="appointment-card-modern__date-time">{formatAppointmentTime(appointment.dateTime)}</div>
                    </div>
                    <div className="appointment-card-modern__main">
                      <div className="appointment-card-modern__header">
                        <div className="appointment-card-modern__avatar-wrapper">
                          <img
                            src={appointment.client?.profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(appointment.client?.name || 'Client')}&size=56&background=random`}
                            alt={appointment.client?.name || 'Client'}
                            className="appointment-card-modern__avatar"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              if (!target.src.includes('ui-avatars.com')) {
                                target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(appointment.client?.name || 'Client')}&size=56&background=random`;
                              }
                            }}
                          />
                        </div>
                        <div className="appointment-card-modern__info">
                          <h3 className="appointment-card-modern__stylist-name">
                            {appointment.client?.name || 'Client'}
                          </h3>
                          <p className="appointment-card-modern__service">{appointment.service?.name || 'Service'}</p>
                          {hasAddons && (
                            <p className="appointment-card-modern__addons">
                              Add-ons ({addons.length}): {addons.map((a) => a.name).filter(Boolean).join(', ') || '—'}
                            </p>
                          )}
                        </div>
                      </div>
                      {appointment.discountSource && appointment.discountSource !== 'none' && (
                        <div style={{ marginTop: '4px' }}>
                          <DiscountBadge
                            discountSource={appointment.discountSource}
                            promotionApplied={appointment.promotionApplied}
                            originalPrice={appointment.service?.originalPrice}
                            finalPrice={appointment.service?.finalPrice}
                            size="sm"
                          />
                        </div>
                      )}
                      <div className="appointment-card-modern__footer">
                        <span className={`appointment-card-modern__status status--${appointment.status || 'pending'}`}>
                          {getAppointmentStatusLabel(appointment.status)}
                        </span>
                        <div style={{ textAlign: 'right' }}>
                          {appointment.service?.originalPrice && appointment.service.originalPrice !== servicePrice && (
                            <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.8em', display: 'block' }}>
                              {formatCurrency(appointment.service.originalPrice)}
                            </span>
                          )}
                          <span className="appointment-card-modern__price">
                            {hasAddons ? formatCurrency(totalPrice) : formatCurrency(servicePrice)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-state-modern">
                <div className="empty-state-modern__icon">
                  <CalendarIcon />
                </div>
                <h3 className="empty-state-modern__title">No Upcoming Appointments</h3>
                <p className="empty-state-modern__description">
                  You're all set! Manage your schedule to book more appointments.
                </p>
                <button 
                  className="empty-state-modern__button"
                  onClick={() => navigate('/stylist/availability')}
                >
                  <CalendarIcon />
                  Manage Schedule
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="sidebar-modern">
          {/* Weekly Overview */}
          <div className="sidebar-section-modern">
            <div className="sidebar-section-modern__header">
              <h3 className="sidebar-section-modern__title">Weekly Overview</h3>
              <button
                className="sidebar-section-modern__action"
                onClick={() => navigate('/stylist/availability')}
              >
                Full Schedule
              </button>
            </div>
            <div className="sidebar-section-modern__content">
              {renderWeeklyCalendar()}
            </div>
          </div>

          {/* Recent Reviews */}
          <div className="sidebar-section-modern">
            <div className="sidebar-section-modern__header">
              <h3 className="sidebar-section-modern__title">Recent Reviews</h3>
            </div>
            <div className="sidebar-section-modern__content">
              {isLoading ? (
                Array(3)
                  .fill(0)
                  .map((_, index) => (
                    <div key={index} className="review-item-modern skeleton"></div>
                  ))
              ) : recentReviews.length > 0 ? (
                recentReviews.map((review) => (
                  <div key={review.id} className="review-item-modern">
                    <div className="review-item-modern__header">
                      <div className="review-item-modern__avatar">
                        <img
                          src={review.clientPhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.clientName)}&size=40&background=random`}
                          alt={review.clientName}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (!target.src.includes('ui-avatars.com')) {
                              target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(review.clientName)}&size=40&background=random`;
                            }
                          }}
                        />
                      </div>
                      <div className="review-item-modern__info">
                        <div className="review-item-modern__name">{review.clientName}</div>
                        <div className="review-item-modern__rating">
                          <div className="review-item-modern__stars">
                            {[...Array(5)].map((_, i) => (
                              <StarIcon 
                                key={i} 
                                size={14}
                                filled={i < review.rating}
                                color={i < review.rating ? '#fbbf24' : '#e5e7eb'}
                              />
                            ))}
                          </div>
                          <div className="review-item-modern__date">
                            {format(new Date(review.date), 'MMM d')}
                          </div>
                        </div>
                      </div>
                    </div>
                    {review.comment && (
                      <div className="review-item-modern__text">
                        {review.comment}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="empty-state-modern">
                  <div className="empty-state-modern__icon">
                    <StarIcon />
                  </div>
                  <h3 className="empty-state-modern__title">No Reviews Yet</h3>
                  <p className="empty-state-modern__description">
                    Keep providing great service to earn reviews from your clients!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StylistDashboardPage;
